import React from 'react';
import { motion } from 'framer-motion';
import { 
  Smartphone, 
  Layers, 
  Cpu, 
  ArrowRight, 
  CheckCircle,
  BellRing,
  ShieldCheck,
  Globe,
  MessageSquare,
  PhoneCall
} from 'lucide-react';
import './MobileAppDev.css';
import FAQAccordion from './FAQAccordion';

const appCapabilities = [
  {
    icon: <Smartphone size={24} />,
    title: 'Cross-Platform Flutter & React Native',
    desc: 'Develop for iOS and Android simultaneously from a single codebase. Native 60 FPS performance, shared business logic, and half the development cost.'
  },
  {
    icon: <Layers size={24} />,
    title: 'Enterprise B2B & Field Operations Apps',
    desc: 'Custom mobile tools for field sales reps, inventory auditing, weighbridge gate passes, doctor portals, and order dispatch teams.'
  },
  {
    icon: <BellRing size={24} />,
    title: 'Push Notifications & Real-Time Sync',
    desc: 'Integrated with Firebase Cloud Messaging (FCM), APNs, and WebSocket protocol for instant order updates, chat alerts, and real-time GPS tracking.'
  },
  {
    icon: <Cpu size={24} />,
    title: 'Offline-First Architecture & SQLite',
    desc: 'Keep your team working even in remote manufacturing plants or weak signal zones with automated local database caching and background server sync.'
  },
  {
    icon: <ShieldCheck size={24} />,
    title: 'Secure Payment Gateway & OTP Auth',
    desc: 'Integrate SMS/WhatsApp OTP authentication, biometric login (Face ID/Fingerprint), Razorpay, PayU, and Stripe payment SDKs securely.'
  },
  {
    icon: <Globe size={24} />,
    title: 'Play Store & App Store Publishing',
    desc: 'Complete handling of Google Play Console and Apple Developer Account submissions, compliance review, screenshots, and App Store Optimization (ASO).'
  }
];

const appProcessDetailed = [
  { num: '01', title: 'Scope & User Persona Mapping', desc: 'We map out target user roles, screen-by-screen feature specs, offline capabilities, and backend server APIs required for your mobile app.' },
  { num: '02', title: 'Figma UI/UX & Clickable Prototype', desc: 'Designing responsive iOS & Android wireframes, micro-interactions, dark/light themes, and interactive click-through prototypes for your review.' },
  { num: '03', title: 'Frontend App & API Coding', desc: 'Developing clean Flutter or React Native code with robust state management (Bloc/Provider), REST/GraphQL API connections, and database integration.' },
  { num: '04', title: 'Real Device QA & Hardware Testing', desc: 'Rigorous testing across 20+ physical Android smartphones and iPhones — testing camera scanners, GPS tracking, biometric login, and offline sync.' },
  { num: '05', title: 'App Store & Play Store Release', desc: 'Preparing App Store Optimization (ASO), privacy policy compliance, screenshot assets, and managing full publishing on Google Play & Apple App Store.' },
  { num: '06', title: 'Crash Analytics & OS Maintenance', desc: 'Setting up Firebase Crashlytics monitoring, 30 days of free post-launch support, and Annual Maintenance Contracts (AMC) for OS compatibility.' }
];

const mobileTech = [
  'Flutter (Dart)', 'React Native', 'Swift (iOS Native)', 'Kotlin (Android Native)',
  'Firebase FCM & Auth', 'SQLite / Realm DB', 'REST & GraphQL APIs', 'Razorpay & Stripe SDK',
  'Biometric Auth', 'Google Maps API', 'WhatsApp Business API', 'App Store Optimization'
];

const appFaqsDetailed = [
  { 
    q: 'How is mobile app development done step-by-step at Wayug Infotech?', 
    a: 'Our process follows 6 clear stages: (1) Discovery & Scope Mapping, (2) Figma UI/UX Design & Prototype, (3) Flutter/Native Sprint Development, (4) QA Testing on Real Physical Devices, (5) Publishing to Google Play & Apple App Store, and (6) Ongoing Maintenance & AMC Support.' 
  },
  { 
    q: 'Should we build with Flutter or Native (Swift/Kotlin)?', 
    a: 'For 90% of business applications, Flutter or React Native is the best choice as it delivers native 60 FPS performance on both iPhones and Android phones from a single codebase, reducing time-to-market and development cost by 50%.' 
  },
  { 
    q: 'How long does mobile app development take from start to finish?', 
    a: 'A standard custom mobile app takes 6 to 10 weeks from initial design prototyping to live release on the App Store and Google Play Store.' 
  },
  { 
    q: 'Will you handle uploading our app to the Apple App Store and Google Play Store?', 
    a: 'Yes! We manage 100% of the app submission process, including developer account setup, screenshot creation, privacy policy compliance, and handling store review feedback until live.' 
  },
  { 
    q: 'Can the app work offline when internet is unavailable?', 
    a: 'Yes! We build offline-first mobile apps using local SQLite/Realm databases that cache data locally on the device and automatically sync with the cloud when internet connection is restored.' 
  },
  { 
    q: 'Do we get full source code ownership and intellectual property rights?', 
    a: 'Absoltely! 100% of the mobile app source code, repository access, design assets, and IP rights belong entirely to your company upon project completion.' 
  },
  { 
    q: 'Do you sign an NDA before we share our app idea?', 
    a: 'Yes, we sign strict Non-Disclosure Agreements (NDAs) before reviewing your project scope or business concept to ensure complete confidentiality.' 
  }
];

export default function MobileAppDev() {
  const whatsappUrl = "https://wa.me/918287999236?text=Hi%20Wayug!%20I%20need%20a%20mobile%20app%20development%20for%20Android%20/%20iOS.";

  return (
    <section className="app-section">
      <div className="container">
        
        {/* Hero */}
        <motion.div 
          className="app-hero"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="app-badge">
            <Smartphone size={16} /> Android &amp; iOS Mobile Apps
          </div>
          <h1 className="app-title">
            Mobile App Development Company in <span className="gradient-text">Delhi NCR &amp; Noida</span>
          </h1>
          <p className="app-subtitle">
            Custom Android &amp; iOS mobile applications engineered for high performance, intuitive UX, offline synchronization, and seamless backend API integration.
          </p>
          <div style={{ marginTop: '2rem', display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <a 
              href={whatsappUrl} 
              target="_blank" 
              rel="noopener noreferrer" 
              className="btn btn-primary"
              style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}
            >
              Discuss Your App Idea <ArrowRight size={16} />
            </a>
            <a href="/contact" className="btn btn-outline">
              Get App Consultation &amp; Estimate
            </a>
          </div>
        </motion.div>

        {/* Feature Grid */}
        <div className="app-grid">
          {appCapabilities.map((item, idx) => (
            <motion.div 
              key={item.title}
              className="app-card"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: idx * 0.08 }}
            >
              <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: 'rgba(124, 58, 237, 0.1)', color: '#a78bfa', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '20px' }}>
                {item.icon}
              </div>
              <h3>{item.title}</h3>
              <p>{item.desc}</p>
            </motion.div>
          ))}
        </div>

        {/* Detailed 6-Step App Development Process */}
        <div style={{ margin: '80px 0' }}>
          <h2 className="text-center" style={{ fontSize: '2.2rem', fontWeight: '800', marginBottom: '12px' }}>
            How Mobile App Development Is Done: <span className="gradient-text">Our 6-Step Process</span>
          </h2>
          <p className="text-center" style={{ color: '#a3a3a3', maxWidth: '700px', margin: '0 auto 40px', fontSize: '1rem', lineHeight: '1.6' }}>
            From initial wireframing to live publishing on Apple App Store &amp; Google Play Store, here is how we bring your mobile application idea to life.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '24px' }}>
            {appProcessDetailed.map((step) => (
              <div key={step.num} style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '16px', padding: '28px' }}>
                <span style={{ fontSize: '2rem', fontWeight: '800', color: '#a78bfa' }}>{step.num}</span>
                <h4 style={{ fontSize: '1.2rem', fontWeight: '700', margin: '12px 0 8px', color: '#fff' }}>{step.title}</h4>
                <p style={{ color: '#a3a3a3', fontSize: '0.9rem', lineHeight: '1.6', margin: 0 }}>{step.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Mobile Tech Stack */}
        <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '20px', padding: '40px', marginBottom: '80px' }}>
          <h3 style={{ fontSize: '1.8rem', fontWeight: '800', textAlign: 'center', marginBottom: '24px' }}>
            Mobile Technology Stack &amp; SDK Integrations
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px' }}>
            {mobileTech.map((t) => (
              <div key={t} style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#e5e5e5', fontSize: '0.95rem' }}>
                <CheckCircle size={16} color="#a78bfa" /> {t}
              </div>
            ))}
          </div>
        </div>

        {/* Extended Mobile FAQs */}
        <FAQAccordion 
          title="Detailed Mobile App Development FAQs" 
          faqs={appFaqsDetailed} 
          accentColor="#a78bfa" 
        />

        {/* Bottom High-Converting Contact Banner */}
        <div style={{ background: 'linear-gradient(135deg, rgba(124,58,237,0.15) 0%, rgba(6,182,212,0.15) 100%)', border: '1px solid rgba(124,58,237,0.3)', borderRadius: '24px', padding: '48px', textAlign: 'center' }}>
          <h2 style={{ fontSize: '2rem', fontWeight: '800', color: '#fff', marginBottom: '12px' }}>Ready to Build Your Mobile App?</h2>
          <p style={{ color: '#a3a3a3', maxWidth: '600px', margin: '0 auto 24px', fontSize: '1rem', lineHeight: '1.6' }}>
            Build a high-performance Android &amp; iOS mobile application with expert developers. Talk directly with our mobile app lead for a free technical consultation.
          </p>
          <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="btn btn-primary" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
              <MessageSquare size={16} /> Chat on WhatsApp (+91 8287999236)
            </a>
            <a href="/contact" className="btn btn-outline" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
              <PhoneCall size={16} /> Send App Inquiry
            </a>
          </div>
        </div>

      </div>
    </section>
  );
}
